import Component, { Topic } from 'component';
import { assert, parseHTML } from 'utils';
import { ReqSection } from 'constants';

var Url;
(function (Url) {
  Url["CHANGE"] = "change";
  Url["ADD"] = "add";
  Url["UPDATE"] = "update";
  Url["BASE"] = "base";
})(Url || (Url = {}));

export const CHANGE_URL_ATTR = 'change-url';
export const ADD_URL_ATTR = 'add-url';
export const UPDATE_URL_ATTR = 'update-url';
export const CART_URL_ATTR = 'cart-url';
export const DRAWER_ATTR = 'drawer';
export const NOTIFICATION_ATTR = 'notification';
export const IS_CART_PAGE_ATTR = 'cart-page';

const URL_ATTS = {
  [Url.CHANGE]: CHANGE_URL_ATTR,
  [Url.ADD]: ADD_URL_ATTR,
  [Url.UPDATE]: UPDATE_URL_ATTR,
  [Url.BASE]: CART_URL_ATTR
};

customElements.define('x-cart-provider', class extends Component {
  constructor() {
    super();
  }

  fetchSections = new Set();
  withNotificator = false;
  ignoreNotifier = false;
  withDrawer = false;

  urls = {
    [Url.CHANGE]: '',
    [Url.ADD]: '',
    [Url.UPDATE]: '',
    [Url.BASE]: ''
  };

  render() {
    this.validateUrls();
    this.initFetchSections();

    // Add to cart (x-product-form publishes CART_ADD with FormData)
    this.sub(
      Topic.CART_ADD,
      ({ formData, TUID, ignoreNotifier }) => {
        this.ignoreNotifier = !!ignoreNotifier;
        this.addToCart(formData, TUID);
      },
      { global: true }
    );

    // Change line item quantity (drawer and cart pages)
    this.sub(
      Topic.CART_CHANGE,
      ({ id, qty, TUID, sectionId }) => {
        if (sectionId) {
          this.fetchSections.add(sectionId);
        }
        this.changeCart(id, qty, TUID);
      },
      { global: true }
    );

    // Cart note updates
    this.sub(
      Topic.CART_NOTES,
      (notes) => {
        fetch(this.urls[Url.UPDATE], {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ note: notes })
        }).catch(console.error);
      },
      { global: true }
    );
  }

  validateUrls() {
    let key;
    for (key in URL_ATTS) {
      if (!this.hasAttribute(URL_ATTS[key])) {
        assert(false, `Cart provider missing ${URL_ATTS[key]} attr`);
      }
      this.urls[key] = this.getAttribute(URL_ATTS[key]);
      if (key !== Url.BASE) {
        this.urls[key] += '.js';
      }
    }
  }

  initFetchSections() {
    if (this.hasAttribute(DRAWER_ATTR)) {
      this.fetchSections.add(ReqSection.CART_DRAWER);
      this.withDrawer = true;
    }
    if (this.hasAttribute(NOTIFICATION_ATTR)) {
      this.withNotificator = true;
    }
  }

  get sectionsList() {
    return Array.from(this.fetchSections).join(',');
  }

  async fetchCart(url, data) {
    if (url !== Url.ADD && url !== Url.CHANGE) {
      console.error('Wrong cart fetch url');
      return;
    }

    const fetchUrl = this.urls[url];
    let res;

    if (url === Url.CHANGE) {
      // Line item updates use JSON payload
      res = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...data,
          sections: this.sectionsList,
          sections_url: this.urls[Url.BASE]
        })
      });
    }

    if (url === Url.ADD) {
      // Adds use FormData so theme and Shopify handle multi item adds
      // Note: for multi item, your form already has items[0][id], items[1][id], etc
      if (this.sectionsList) {
        data.append('sections', this.sectionsList);
      }

      res = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: data
      });
    }

    const rData = await res.json();

    if (!res.ok) {
      // Try to surface Shopify errors nicely
      const message =
        (rData && (rData.description || rData.message)) ||
        'Cart operation failed';
      throw new Error(message);
    }

    return rData;
  }

  handleError(TUID, error) {
    console.error(error);

    this.pub(Topic.CART_UPDATE, {
      TUID,
      error: error.message
    });

    if (typeof error.message === 'string') {
      this.pub(Topic.SHOW_TOAST, {
        type: 'error',
        msg: error.message
      });
    }
  }

  getSectionsData(data) {
    const res = {};

    if (!data || !data.sections) {
      return res;
    }

    Object.keys(data.sections).forEach((sectionKey) => {
      res[sectionKey] = parseHTML(data.sections[sectionKey]);
    });

    return res;
  }

  async changeCart(id, quantity, TUID) {
    try {
      const data = await this.fetchCart(Url.CHANGE, { id, quantity });

      this.pub(Topic.CART_UPDATE, {
        TUID,
        data: {
          indicator: Number(data['item_count']) > 0,
          openDrawer: false,
          sections: this.getSectionsData(data)
        }
      });
    } catch (e) {
      this.handleError(TUID, e);
    }
  }

  async addToCart(formData, TUID) {
  // Start with "we intend to use notifier" if it is enabled
  let useNotifier = this.withNotificator && !this.ignoreNotifier;

  try {
    const data = await this.fetchCart(Url.ADD, formData);
    const sections = this.getSectionsData(data);

    // Figure out which product URL to use for the notifier
    // 1) Standard single item add: data.url
    // 2) Multi item add: try data.items[0].url if present
    let productUrl = data && data.url ? data.url : null;

    if (!productUrl && Array.isArray(data?.items) && data.items.length > 0) {
      // Some Shopify responses for multi adds attach url on each item
      if (data.items[0].url) {
        productUrl = data.items[0].url;
      } else if (data.items[0].product && data.items[0].product.handle) {
        // Fallback guess if theme needs a URL and Shopify only gave product info
        // This keeps existing behavior for most themes and gives us a decent URL
        productUrl = `/${data.items[0].product.handle}`;
      }
    }

    // Only try to fetch notifier HTML if we actually have a URL
    if (this.withNotificator && !this.ignoreNotifier && productUrl) {
      const notifierData = await this.getNotifierData(productUrl);
      if (notifierData) {
        useNotifier = true;
        sections[ReqSection.PRODUCT_NOTIFIER] = notifierData;
      }
    } else if (!productUrl) {
      // No usable URL from Shopify, so do not pretend a notifier will show
      useNotifier = false;
    }

    // If there is no notifier and no drawer, go to cart page
    if (!useNotifier && !this.withDrawer) {
      window.location.href = this.urls[Url.BASE];
    }

    // Update cart UI (drawer and notifier section) via sections
    this.pub(Topic.CART_UPDATE, {
      TUID,
      data: {
        indicator: !!data,
        openDrawer: !useNotifier,
        sections
      }
    });
  } catch (e) {
    this.handleError(TUID, e);
  }
}

  async getNotifierData(productUrl) {
    try {
      const nres = await fetch(
        productUrl + `&sections=${ReqSection.PRODUCT_NOTIFIER}`
      );
      const ndata = await nres.json();
      return parseHTML(ndata[ReqSection.PRODUCT_NOTIFIER]);
    } catch (e) {
      console.error(e);
      return null;
    }
  }
});